<template>
  <div class="all-recipes-list">
    <div class="all-recipes-header">
      <div class="h8">TOUTES LES RECETTES</div>
      <div class="header-controls">
        <b-button size="sm" variant="outline-primary" @click="refreshRecipes" class="refresh-btn">
          <b-icon icon="arrow-clockwise"></b-icon> Actualiser
        </b-button>
        <b-dropdown id="dropdown-1" text="Catégories" class="m-md-2" size="sm" variant="primary" right>
          <b-dropdown-item
            v-for="category in categories" 
            :key="category.id"
            @click="categorySelected(category.id)"
          >
            {{ category.nom }}
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>
    
    <!-- Search Bar -->
    <div class="search-container">
      <b-input-group>
        <b-form-input 
          v-model="searchQuery" 
          placeholder="Rechercher une recette par nom..."
          @input="filterRecipes"
          class="search-input"
        ></b-form-input>
        <b-input-group-append>
          <b-button variant="primary" class="search-btn">
            <b-icon icon="search"></b-icon>
          </b-button>
        </b-input-group-append>
      </b-input-group>
      
      <!-- Clear search button (visible only when searching) -->
      <b-button 
        v-if="searchQuery" 
        variant="outline-secondary" 
        size="sm" 
        class="clear-btn"
        @click="clearSearch"
      >
        <b-icon icon="x"></b-icon> Effacer
      </b-button>
    </div>
    
    <!-- Results info -->
    <div v-if="searchQuery || selectedCategoryId !== 0" class="results-info">
      <small class="text-muted">
        {{ displayRecipes.length }} recette(s) trouvée(s)
        <span v-if="searchQuery"> pour "{{ searchQuery }}"</span>
        <span v-if="selectedCategoryId !== 0"> dans {{ getSelectedCategoryName() }}</span>
      </small>
    </div>

    <div class="recipes-grid">
      <div 
        v-for="recipe in displayRecipes" 
        :key="`recipe-${recipe.id}-${recipe.name}`" 
        @click="recipeSelected(recipe.id)"
        class="recipe-grid-item"
      >
        <RecipeCard :recipe="recipe" />
      </div>
    </div>

    <!-- Empty state when no recipes found -->
    <div v-if="displayRecipes.length === 0" class="empty-state">
      <div class="empty-icon">🔍</div>
      <h5>Aucune recette trouvée</h5>
      <p v-if="searchQuery">Aucune recette ne correspond à votre recherche "{{ searchQuery }}"</p>
      <p v-else-if="selectedCategoryId !== 0">Aucune recette dans cette catégorie</p>
      <b-button variant="outline-primary" @click="clearFilters" size="sm">
        Voir toutes les recettes
      </b-button>
    </div>
  </div>
</template>

<script>
import FirebaseDataService from '../services/FirebaseDataService.js';
import RecipeCard from '../components/RecipeCard.vue'

export default {
  name: 'AllRecipesList',
  components: {
    RecipeCard
  },
  data() {
    return {
      categories: [],
      displayRecipes: [],
      allRecipes: [], // Store all recipes for filtering
      searchQuery: '',
      selectedCategoryId: 0
    };
  },
  async mounted() {
    await this.fetchCategories();
    await this.fetchRecipes();
  },
  watch: {
    // Refresh recipes when route changes (e.g., coming back from Admin)
    '$route'(to, from) {
      if (to.name === 'Home' || to.path === '/') {
        this.fetchRecipes();
      }
    }
  },
  methods: {
    async fetchCategories() {
      try {
        this.categories = await FirebaseDataService.getCategories();
        this.categories.unshift({ nom: "Toutes les catégories", id: 0 });
      } catch (error) {
        console.error('Erreur lors du chargement des catégories:', error);
      }
    },
    
    async fetchRecipes() {
      try {
        console.log('Fetching recipes...');
        const allRecipes = await FirebaseDataService.getRecipes();
        console.log('All recipes from Firebase:', allRecipes);
        
        this.allRecipes = allRecipes.filter(recipe => {
          const isPublished = recipe.published !== false; // Default to true if not set
          const publicationDate = new Date(recipe.datePublication);
          const now = new Date();
          const shouldShow = isPublished && publicationDate <= now;
          
          console.log(`Recipe "${recipe.name}": published=${isPublished}, date=${publicationDate.toISOString()}, shouldShow=${shouldShow}`);
          return shouldShow;
        });
        
        this.displayRecipes = [...this.allRecipes];
        console.log('Filtered recipes to display:', this.displayRecipes);
      } catch (error) {
        console.error('Erreur lors du chargement des recettes:', error);
      }
    },
    
    async refreshRecipes() {
      console.log('Manual refresh triggered');
      await this.fetchRecipes();
    },
    
    categorySelected(categoryId) {
      this.selectedCategoryId = categoryId;
      this.filterRecipes();
    },
    filterRecipes() {
      let filtered = [...this.allRecipes];

      // Filter by category first
      if (this.selectedCategoryId !== 0) {
        filtered = filtered.filter(recipe => 
          recipe.categories.some(c => c === this.selectedCategoryId)
        );
      }

      // Then filter by search query (accent-insensitive)
      if (this.searchQuery.trim()) {
        const query = this.normalizeString(this.searchQuery.toLowerCase().trim());
        filtered = filtered.filter(recipe =>
          this.normalizeString(recipe.name.toLowerCase()).includes(query)
        );
      }

      this.displayRecipes = filtered;
    },
    normalizeString(str) {
      // Remove accents and diacritics for search
      return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    },
    clearSearch() {
      this.searchQuery = '';
      this.filterRecipes();
    },
    clearFilters() {
      this.searchQuery = '';
      this.selectedCategoryId = 0;
      this.filterRecipes();
    },
    getSelectedCategoryName() {
      const category = this.categories.find(c => c.id === this.selectedCategoryId);
      return category ? category.nom : '';
    },
    recipeSelected(recipeId) {
      this.$router.push(`/recipe/${recipeId}`)
    }
  }
}
</script>

<style scoped>
.all-recipes-list {
  padding: 1rem;
}

.all-recipes-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.refresh-btn {
  white-space: nowrap;
}

/* ============= SEARCH CONTAINER ============= */
.search-container {
  margin-bottom: 1rem;
  position: relative;
}

.search-input {
  border-radius: 25px 0 0 25px;
  border-right: none;
  padding-left: 20px;
}

.search-btn {
  border-radius: 0 25px 25px 0;
  border-left: none;
  padding: 0 15px;
}

.clear-btn {
  position: absolute;
  top: 50%;
  right: 60px;
  transform: translateY(-50%);
  z-index: 3;
  border-radius: 15px;
  padding: 3px 8px;
}

/* ============= RESULTS INFO ============= */
.results-info {
  margin-bottom: 1rem;
  padding: 0.5rem 1rem;
  background-color: #f8f9fa;
  border-radius: 20px;
  border-left: 3px solid var(--primary);
}

/* ============= RECIPES GRID ============= */
.recipes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.recipe-grid-item {
  cursor: pointer;
  transition: transform 0.2s ease;
}

.recipe-grid-item:hover {
  transform: translateY(-2px);
}

/* ============= EMPTY STATE ============= */
.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  background-color: #f8f9fa;
  border-radius: 15px;
  margin-top: 2rem;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.6;
}

.empty-state h5 {
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.empty-state p {
  color: #868e96;
  margin-bottom: 1.5rem;
}

/* ============= RESPONSIVE DESIGN ============= */
@media (max-width: 768px) {
  .all-recipes-header {
    flex-direction: column;
    gap: 0.5rem;
    align-items: stretch;
  }
  
  .all-recipes-header .h8 {
    text-align: center;
    margin-bottom: 0.5rem;
  }
  
  .search-container {
    margin-bottom: 1.5rem;
  }
  
  .search-input {
    font-size: 16px; /* Prevents zoom on iOS */
  }
  
  .clear-btn {
    position: static;
    transform: none;
    margin-top: 0.5rem;
    align-self: flex-start;
  }
  
  .recipes-grid {
    grid-template-columns: 1fr;
    gap: 0.8rem;
  }
  
  .results-info {
    text-align: center;
    padding: 0.8rem;
  }
  
  .empty-state {
    padding: 2rem 1rem;
  }
  
  .empty-icon {
    font-size: 3rem;
  }
}

@media (max-width: 480px) {
  .all-recipes-list {
    padding: 0.5rem;
  }
  
  .search-input {
    padding-left: 15px;
  }
  
  .search-btn {
    padding: 0 12px;
  }
  
  .recipes-grid {
    grid-template-columns: 1fr;
  }
}

/* ============= FOCUS STATES FOR ACCESSIBILITY ============= */
.search-input:focus {
  box-shadow: 0 0 0 0.2rem rgba(var(--primary-rgb), 0.25);
  border-color: var(--primary);
}

.clear-btn:focus,
.search-btn:focus {
  box-shadow: 0 0 0 0.2rem rgba(var(--primary-rgb), 0.25);
}
</style>